import { Box, Collapse, Group, useMantineTheme } from '@mantine/core'
import { useEffect, useState } from 'react'
import { IconType } from 'react-icons'
import { TbChevronLeft, TbChevronRight } from 'react-icons/tb'
import { Link, matchPath, useLocation } from 'react-router-dom'
import { useCustomizerActions, useCustomizerStore } from 'store/customizerStore'
import { SidebarLabel } from './SidebarLabel'
import classes from './LinksGroup.module.css'

export interface LinkCollapse {
  label: string
  link: string
}

interface LinksGroupProps {
  icon: IconType
  initiallyOpened?: boolean
  label: string
  href?: string
  links?: LinkCollapse[]
}

export function LinksGroup({
  icon: Icon,
  label,
  initiallyOpened = false,
  href,
  links,
}: LinksGroupProps) {
  const theme = useMantineTheme()
  const location = useLocation()

  const mobileMenuOpened = useCustomizerStore(state => state.mobileMenuOpened)
  const { toggleMobileMenu } = useCustomizerActions()

  const hasLinks = Array.isArray(links)
  const [opened, setOpened] = useState(initiallyOpened || false)
  const ChevronIcon = theme.dir === 'ltr' ? TbChevronRight : TbChevronLeft
  const isGroupActive = Boolean(
    hasLinks &&
      links.some(link =>
        matchPath({ path: link.link, end: true }, location.pathname),
      ),
  )

  useEffect(() => {
    if (isGroupActive) setOpened(true)
  }, [isGroupActive])

  const items = (hasLinks ? links : []).map(link => {
    const isActive = Boolean(
      matchPath({ path: link.link, end: true }, location.pathname),
    )

    return (
      <Link
        className={[classes.sublink, 'link-label', isActive && 'is-active']
          .filter(Boolean)
          .join(' ')}
        to={link.link}
        key={link.label}
        onClick={() => {
          if (mobileMenuOpened) toggleMobileMenu()
        }}
        data-testid={`navigate-${link.link.substring(1)}`}
      >
        {link.label}
      </Link>
    )
  })

  const controlClassName = [
    classes.control,
    isGroupActive && 'is-active',
    'nav-link',
  ]
    .filter(Boolean)
    .join(' ')

  const content = (
    <Group
      className={[classes.link, 'nav-link-content'].join(' ')}
      justify="space-between"
      gap={0}
      wrap="nowrap"
    >
      <Box className={classes.linkContent}>
        {Icon && <Icon size={24} />}
        <SidebarLabel
          label={label}
          className={[classes.navLink, 'link-label'].join(' ')}
        />
      </Box>
      {hasLinks && (
        <ChevronIcon
          className={[classes.chevron, 'nav-chevron'].join(' ')}
          size={14}
          style={{
            transform: opened
              ? `rotate(${theme.dir === 'rtl' ? -90 : 90}deg)`
              : 'none',
          }}
        />
      )}
    </Group>
  )

  const control =
    href && !hasLinks ? (
      <Link
        to={href}
        className={controlClassName}
        aria-label={label}
        title={label}
        onClick={() => {
          if (mobileMenuOpened) toggleMobileMenu()
        }}
        data-testid={`navigate-${label.toLowerCase()}`}
      >
        {content}
      </Link>
    ) : (
      <button
        type="button"
        onClick={() => setOpened(o => !o)}
        className={controlClassName}
        aria-label={label}
        title={label}
        aria-expanded={hasLinks ? opened : undefined}
        data-testid={`navigate-${label.toLowerCase()}`}
      >
        {content}
      </button>
    )

  return (
    <>
      {control}
      {hasLinks ? (
        <Collapse className="nav-collapse" expanded={opened}>
          {items}
        </Collapse>
      ) : null}
    </>
  )
}
